'use client';

import type { ReactNode } from 'react';
import React, { createContext, useContext, useState, useEffect } from 'react';
import { setGlobalWorkspaceId } from '~/utils/workspaceLink';

interface WorkspaceContextValue {
  workspaceId: string;
  workspaceName: string;
  isLoading: boolean;
  hasWorkspaceAccess: boolean;
  defaultWorkspaceId: string | null;
  setWorkspaceId: (id: string) => void;
  setWorkspaceName: (name: string) => void;
}

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null);

interface WorkspaceProviderProps {
  children: ReactNode;
  workspaceId: string;
  workspaceName: string;
  isLoading?: boolean;
  hasWorkspaceAccess?: boolean;
  defaultWorkspaceId?: string | null;
}

export function WorkspaceProvider({
  children,
  workspaceId: initialWorkspaceId,
  workspaceName: initialWorkspaceName,
  isLoading = false,
  hasWorkspaceAccess = false,
  defaultWorkspaceId = null,
}: WorkspaceProviderProps) {
  const [workspaceId, setWorkspaceId] = useState(initialWorkspaceId);
  const [workspaceName, setWorkspaceName] = useState(initialWorkspaceName);

  useEffect(() => {
    setWorkspaceId(initialWorkspaceId);
  }, [initialWorkspaceId]);

  useEffect(() => {
    setWorkspaceName(initialWorkspaceName);
  }, [initialWorkspaceName]);

  // Sync workspaceId with global state for tRPC link
  useEffect(() => {
    setGlobalWorkspaceId(workspaceId);
  }, [workspaceId]);

  return (
    <WorkspaceContext.Provider
      value={{
        workspaceId,
        workspaceName,
        isLoading,
        hasWorkspaceAccess,
        defaultWorkspaceId,
        setWorkspaceId,
        setWorkspaceName,
      }}
    >
      {children}
    </WorkspaceContext.Provider>
  );
}

export function useWorkspace() {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error('useWorkspace must be used within WorkspaceProvider');
  }
  return context;
}

export function useWorkspaceSafe() {
  return useContext(WorkspaceContext);
}
